import React, { useState, useEffect, useCallback } from "react";
import { Calendar, Users, Edit2, Save } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  getOfficeTime,
  upsertOfficeTime,
  getAllStaffAttendance,
  adminMarkAttendanceOwner,
} from "../api/staffAttendanceApi";

import {
  Card,
  CardContent,
  Button,
  Input,
  Textarea,
  Select,
  Badge,
  Modal,
  Alert,
  Skeleton,
  EmptyState,
} from "../components/ui";
import { useToast } from "../context/ToastContext";

const STATUS_VARIANTS = {
  PRESENT: "success",
  ABSENT: "danger",
  LATE: "warning",
  HALF_DAY: "warning",
  HOLIDAY: "primary",
  LEAVE: "default",
};

const STATUS_OPTIONS = ["PRESENT", "ABSENT", "LATE", "HALF_DAY", "HOLIDAY", "LEAVE"];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const todayString = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatTime12hr = (time24) => {
  if (!time24) return "--";
  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

const formatMinutesToHours = (totalMinutes) => {
  const mins = Number(totalMinutes);
  if (isNaN(mins) || mins <= 0) return "—";
  const hours = Math.floor(mins / 60);
  const remainingMinutes = mins % 60;

  if (hours === 0) return `${remainingMinutes} mins`;
  if (remainingMinutes === 0) return `${hours} ${hours === 1 ? "hr" : "hrs"}`;
  return `${hours} ${hours === 1 ? "hr" : "hrs"} ${remainingMinutes} mins`;
};

const StatusBadge = ({ status }) => (
  <Badge variant={STATUS_VARIANTS[status] || "default"} className="text-[10px] font-semibold px-2 py-0.5">
    {status ? status.replace("_", " ") : "—"}
  </Badge>
);

const StaffAttendance = () => {
  useAuth();

  // Office time settings
  const [officeTime, setOfficeTime] = useState({ startTime: "09:00", endTime: "17:00", gracePeriodMinutes: 15 });
  const [otLoading, setOtLoading] = useState(true);
  const [otSaving, setOtSaving] = useState(false);
  const toast = useToast();

  // View mode + filters
  const [viewMode, setViewMode] = useState("date"); // "date" | "month"
  const [selectedDate, setSelectedDate] = useState(todayString());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // Records
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  // Mark modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalForm, setModalForm] = useState({
    staffId: "",
    date: "",
    status: "PRESENT",
    checkInTime: "",
    checkOutTime: "",
    remarks: "",
  });
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState("");
// Office time fetching
  const fetchOfficeTime = useCallback(async () => {
    try {
      setOtLoading(true);
      const data = await getOfficeTime();
      if (data?.officeTime) setOfficeTime(data.officeTime);
    } catch (err) {
      toast.error("Failed to load office time settings");
    } finally {
      setOtLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOfficeTime();
  }, [fetchOfficeTime]);

  const handleOfficeTimeSave = async (e) => {
    e.preventDefault();
    try {
      setOtSaving(true);
      await upsertOfficeTime({
        startTime: officeTime.startTime,
        endTime: officeTime.endTime,
        gracePeriodMinutes: Number(officeTime.gracePeriodMinutes),
      });
      toast.success("Office time updated");
      
    } catch (err) {
      toast.error("Failed to save office time");
    } finally {
      setOtSaving(false);
    }
  };

  // Attendance records fetching
  const fetchAttendance = useCallback(async () => {
    try {
      setLoading(true);
      const filters =
        viewMode === "date"
          ? { date: selectedDate }
          : { year: selectedYear, month: selectedMonth };
      const data = await getAllStaffAttendance(filters);
      setRecords(data?.records || []);
    } catch (err) {
      toast.error("Failed to load attendance records");
    } finally {
      setLoading(false);
    }
  }, [viewMode, selectedDate, selectedYear, selectedMonth]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  const summary = records.reduce(
    (acc, r) => {
      const key = (r.status || "").toLowerCase();
      if (key === "present") acc.present += 1;
      else if (key === "absent") acc.absent += 1;
      else if (key === "late") acc.late += 1;
      else if (key === "half_day") acc.halfDay += 1;
      else if (key === "holiday") acc.holiday += 1;
      else if (key === "leave") acc.leave += 1;
      return acc;
    },
    { present: 0, absent: 0, late: 0, halfDay: 0, holiday: 0, leave: 0 }
  );

  // Mark modal handlers
  const openMarkModal = (record) => {
    setModalError("");
    setModalForm({
      staffId: record?.staffId?._id || record?.staffId || "",
      date: record?.date || selectedDate,
      status: record?.status || "PRESENT",
      checkInTime: record?.checkInTime || "",
      checkOutTime: record?.checkOutTime || "",
      remarks: record?.remarks || "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalError("");
  };

  const handleModalSave = async () => {
    if (!modalForm.staffId || !modalForm.date) {
      setModalError("Staff and date are required");
      return;
    }
    try {
      setModalSaving(true);
      setModalError("");
      await adminMarkAttendanceOwner(modalForm);
      setModalOpen(false);
      fetchAttendance();
    } catch (err) {
      setModalError("Failed to save attendance record");
    } finally {
      setModalSaving(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 antialiased">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            Staff Attendance
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            Manage office hours and review staff attendance
          </p>
        </div>
      </div>

      {/* Office Time Settings */}
      <div className="space-y-3">
        <h2 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
          Office Time Settings
        </h2>

        
        

        <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
          <CardContent className="p-4 sm:p-6">
            {otLoading ? (
              <Skeleton height={40} width="100%" />
            ) : (
              <form onSubmit={handleOfficeTimeSave} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                <Input
                  label="Start Time"
                  type="time"
                  value={officeTime.startTime}
                  onChange={(e) => setOfficeTime({ ...officeTime, startTime: e.target.value })}
                  required
                />
                <Input
                  label="End Time"
                  type="time"
                  value={officeTime.endTime}
                  onChange={(e) => setOfficeTime({ ...officeTime, endTime: e.target.value })}
                  required
                />
                <Input
                  label="Grace Period (minutes)"
                  type="number"
                  min="0"
                  value={officeTime.gracePeriodMinutes}
                  onChange={(e) => setOfficeTime({ ...officeTime, gracePeriodMinutes: e.target.value })}
                  required
                />
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={otSaving}
                  className="w-full text-xs font-semibold py-2.5"
                >
                  {otSaving ? "Saving…" : "Save Settings"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Attendance View */}
      <div className="space-y-3">
        <h2 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
          Attendance Records
        </h2>

        <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
          <CardContent className="p-4 sm:p-6 space-y-5">
            {/* Filter controls */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-1 bg-neutral-100 dark:bg-neutral-800 p-1 rounded-lg border border-neutral-200/50 self-start sm:self-auto">
                <button
                  type="button"
                  onClick={() => setViewMode("date")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${ viewMode === "date" ? "bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 shadow-xs" : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-100" }`}
                >
                  By Date
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("month")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${ viewMode === "month" ? "bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 shadow-xs" : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-100" }`}
                >
                  By Month
                </button>
              </div>

              {viewMode === "date" ? (
                <div className="w-full sm:w-auto">
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    leftIcon={<Calendar className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />}
                    className="text-xs"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="text-xs"
                  >
                    {MONTH_NAMES.map((m, idx) => (
                      <option key={m} value={idx + 1}>
                        {m}
                      </option>
                    ))}
                  </Select>
                  <Select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="text-xs"
                  >
                    {[selectedYear - 1, selectedYear, selectedYear + 1].map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
            </div>

            {/* Monthly Summary Cards */}
            {viewMode === "month" && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  ["Present", summary.present, "bg-emerald-50/60 border-emerald-100 text-emerald-800"],
                  ["Absent", summary.absent, "bg-rose-50/60 border-rose-100 text-rose-800"],
                  ["Late", summary.late, "bg-amber-50/60 border-amber-100 text-amber-800"],
                  ["Half Day", summary.halfDay, "bg-orange-50/60 border-orange-100 text-orange-800"],
                  ["Holiday", summary.holiday, "bg-sky-50/60 border-sky-100 text-sky-800"],
                  ["Leave", summary.leave, "bg-purple-50/60 border-purple-100 text-purple-800"],
                ].map(([label, value, cls]) => (
                  <div key={label} className={`rounded-xl border p-3 text-center ${cls}`}>
                    <p className="text-xl font-bold tracking-tight">{value}</p>
                    <p className="text-[11px] font-medium opacity-80">{label}</p>
                  </div>
                ))}
              </div>
            )}

            

            {/* Records Table */}
            {loading ? (
              <div className="space-y-2 py-4">
                <Skeleton height={40} width="100%" />
                <Skeleton height={40} width="100%" />
                <Skeleton height={40} width="100%" />
              </div>
            ) : records.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No attendance records found"
                description="Try adjusting your date or month filter parameters above."
              />
            ) : (
              <div className="overflow-x-auto border border-neutral-200 dark:border-neutral-800 rounded-xl">
                <table className="w-full text-left text-xs">
                  <thead className="bg-neutral-50/80 border-b border-neutral-200 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400 uppercase tracking-wider font-semibold">
                    <tr>
                      <th className="py-3 px-4">Staff Name</th>
                      <th className="py-3 px-4">Staff Code</th>
                      {viewMode === "month" && <th className="py-3 px-4">Date</th>}
                      <th className="py-3 px-4">Check In</th>
                      <th className="py-3 px-4">Check Out</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Late By</th>
                      <th className="py-3 px-4">Remarks</th>
                      <th className="py-3 px-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800 text-neutral-700 dark:text-neutral-200">
                    {records.map((r) => (
                      <tr
                        key={r._id}
                        className="hover:bg-neutral-50/80 transition-colors cursor-pointer"
                        onClick={() => openMarkModal(r)}
                      >
                        <td className="py-3 px-4 font-semibold text-neutral-900 dark:text-neutral-100">{r.staffId?.name || "—"}</td>
                        <td className="py-3 px-4 text-neutral-500 dark:text-neutral-400">{r.staffId?.staffCode || "—"}</td>
                        {viewMode === "month" && <td className="py-3 px-4 font-medium">{r.date}</td>}
                        <td className="py-3 px-4">{formatTime12hr(r.checkInTime)}</td>
                        <td className="py-3 px-4">{formatTime12hr(r.checkOutTime)}</td>
                        <td className="py-3 px-4">
                          <StatusBadge status={r.status} />
                        </td>
                        <td className="py-3 px-4">
                          {formatMinutesToHours(r.lateByMinutes)}
                        </td>
                        <td className="py-3 px-4 max-w-[160px] truncate text-neutral-500 dark:text-neutral-400">{r.remarks || "—"}</td>
                        <td className="py-3 px-4 text-right">
                          <Button variant="ghost" size="xs" className="p-1 h-7 w-7">
                            <Edit2 className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Admin Mark Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title="Mark Attendance"
        size="md"
      >
        <div className="space-y-4 py-1">
          {modalError && <Alert variant="danger">{modalError}</Alert>}

          <Input
            label="Date"
            type="date"
            value={modalForm.date}
            onChange={(e) => setModalForm({ ...modalForm, date: e.target.value })}
          />

          <Select
            label="Status"
            value={modalForm.status}
            onChange={(e) => setModalForm({ ...modalForm, status: e.target.value })}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ")}
              </option>
            ))}
          </Select>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Check In"
              type="time"
              value={modalForm.checkInTime}
              onChange={(e) => setModalForm({ ...modalForm, checkInTime: e.target.value })}
            />
            <Input
              label="Check Out"
              type="time"
              value={modalForm.checkOutTime}
              onChange={(e) => setModalForm({ ...modalForm, checkOutTime: e.target.value })}
            />
          </div>

          <Textarea
            label="Remarks"
            value={modalForm.remarks}
            onChange={(e) => setModalForm({ ...modalForm, remarks: e.target.value })}
            rows={3}
            placeholder="Optional remarks..."
          />

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-neutral-100 dark:border-neutral-800">
            <Button type="button" variant="ghost" size="sm" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              isLoading={modalSaving}
              onClick={handleModalSave}
              leftIcon={<Save className="w-3.5 h-3.5" />}
            >
              Save Record
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StaffAttendance;